// Epic 8 working surface — the Greenhold design-system gallery.
//
// One chromeless route (/design-system) that renders the canonical
// tokens + every cheap-to-fixture component as labeled specimens, and
// links out to the heavier /prototype/* sandboxes (ceremony, dossier,
// certificate). Open it beside the code (VSCode "Simple Browser: Show"
// → http://localhost:3000/design-system) — vite hot-reloads it, so
// token/component edits are visible the moment they save.
//
// Token values are duplicated from packages/tokens/tailwind-preset.cjs
// (the source of truth). That duplication is the established tokens
// contract — "drift is caught by visual review in /prototype/* routes"
// — and THIS page is now that review surface.

import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  PatronageCertificate,
  Separator,
  Skeleton,
  ParchmentPost,
  SealedDossier,
  type SealedDossierData,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TierBadge,
} from "@greenhold/components";

import { EditorialCard } from "../features/feed/EditorialCard";
import { CreatorPostCard } from "../features/feed/CreatorPostCard";
import { SilenceCard } from "../features/feed/SilenceCard";
import { GreenholdFrame, PinnedChip } from "../features/feed/GreenholdFrame";
import type { FeedCard } from "../features/feed/useFeed";

// ---------------------------------------------------------------------------
// Token catalogs (mirror tailwind-preset.cjs — keep in sync by eye)
// ---------------------------------------------------------------------------

const RAMPS: { name: string; prefix: string; steps: [string, string][] }[] = [
  {
    name: "Forest",
    prefix: "brand-forest",
    steps: [
      ["50", "#e9f5ee"], ["100", "#cfe5d8"], ["200", "#a8d0b9"],
      ["300", "#7bb796"], ["400", "#4f9e74"], ["500", "#1f7a4d"],
      ["600", "#196340"], ["700", "#134d32"], ["800", "#0e3725"],
      ["900", "#0e2a1f"],
    ],
  },
  {
    name: "Gold",
    prefix: "brand-gold",
    steps: [
      ["50", "#faf3e3"], ["100", "#f3e3b8"], ["200", "#e9cf85"],
      ["300", "#dcb957"], ["400", "#cea33d"], ["500", "#c39235"],
      ["600", "#9d7426"], ["700", "#7a5a1d"], ["800", "#5a4115"],
      ["900", "#3d2c0e"],
    ],
  },
  {
    name: "Paper",
    prefix: "brand-paper",
    steps: [
      ["50", "#fdfbf5"], ["100", "#faf8f2"], ["200", "#f0ebd8"],
      ["300", "#e9e4d6"],
    ],
  },
  {
    name: "Ink",
    prefix: "brand-ink",
    steps: [
      ["700", "#3a2f1c"], ["800", "#2e2310"], ["900", "#1f1408"],
    ],
  },
  {
    name: "Wax — pressed bordeaux seal (dossier + ceremony only)",
    prefix: "brand-wax",
    steps: [
      ["400", "#a83a42"], ["500", "#8c2430"], ["600", "#7e1f2a"],
      ["700", "#6e1a24"], ["ink", "#5f141d"], ["deep", "#4a0d15"],
    ],
  },
  {
    name: "Parchment — aged-paper ground (gatefold + ParchmentPost)",
    prefix: "brand-parchment",
    steps: [
      ["hi", "#ece4cc"], ["DEFAULT", "#e7dec4"], ["lo", "#ddd0ad"],
      ["deep", "#d6c9a4"], ["ink", "#3a2f1c"],
    ],
  },
];

const TIER_TRIOS: { tier: string; fill: string; fg: string; accent: string }[] = [
  { tier: "common", fill: "#e9e4d6", fg: "#1f2d24", accent: "#7bb796" },
  { tier: "preferred", fill: "#2a5da0", fg: "#eaf1fa", accent: "#5e8ec5" },
  { tier: "premium", fill: "#5b21b6", fg: "#f5f3ff", accent: "#8b5cf6" },
  { tier: "founders", fill: "#c39235", fg: "#1f1408", accent: "#e9cf85" },
];

const STATE_TRIOS: { state: string; fill: string; fg: string; accent: string }[] = [
  { state: "error", fill: "#b22d26", fg: "#faefef", accent: "#dc4a43" },
  { state: "warning", fill: "#b6852a", fg: "#2e2310", accent: "#dca64a" },
  { state: "success", fill: "#1f7a4d", fg: "#e9f5ee", accent: "#4f9e74" },
  { state: "info", fill: "#2a5da0", fg: "#eaf1fa", accent: "#5e8ec5" },
];

const SHADOWS = [
  "shadow-sm", "shadow", "shadow-md", "shadow-lg", "shadow-xl",
  "shadow-tier-glow-sm", "shadow-tier-glow-md", "shadow-tier-glow-lg",
  "shadow-founders-lift",
];

const RADII = [
  "rounded-sm", "rounded", "rounded-md", "rounded-lg", "rounded-xl",
  "rounded-2xl", "rounded-full",
];

const PROTOTYPES: { path: string; label: string }[] = [
  { path: "/prototype/reveal-ceremony", label: "RevealCeremony (the keystone)" },
  { path: "/prototype/sealed-dossier", label: "SealedDossier (5-state machine)" },
  { path: "/prototype/certificate", label: "Certificate (hero artifact)" },
  { path: "/prototype/dossier-shop-card", label: "DossierShopCard" },
  { path: "/prototype/tier-badge", label: "TierBadge (full matrix)" },
  { path: "/prototype/creator-earnings-ticker", label: "CreatorEarningsTicker" },
  { path: "/prototype/origin-link-footer", label: "OriginLinkFooter" },
];

// ---------------------------------------------------------------------------
// Feed-card fixtures
// ---------------------------------------------------------------------------

function feedFixture(overrides: Partial<FeedCard>): FeedCard {
  return {
    id: "ds-fixture",
    source_kind: "admin_editorial",
    creator_user_id: null,
    creator_handle: null,
    creator_display_name: null,
    title: "",
    body: null,
    media_url: null,
    external_url: null,
    published_at: new Date(Date.now() - 2 * 3600_000).toISOString(),
    is_pinned: false,
    is_promo: false,
    source_specific: {},
    ...overrides,
  };
}

const EDITORIAL_FIXTURE = feedFixture({
  id: "ds-editorial",
  title: "First dossier goes live Friday",
  body: "The CS cohort opens with twenty creators. Get your account ready — editions are numbered.",
});

const CREATOR_POST_FIXTURE = feedFixture({
  id: "ds-creator-post",
  source_kind: "creator_post",
  creator_user_id: "00000000-0000-0000-0000-000000000001",
  creator_handle: "nova",
  creator_display_name: "Nova",
  body: "Scrims all week. If the aim holds, Friday's clips are going to be a problem.",
});

const SILENCE_FIXTURE = feedFixture({ id: "ds-silence" });

// Sealed-dossier fixtures for the parchment-gatefold specimens.
const DS_LIVE_DOSSIER: SealedDossierData = {
  id: "aaaaaaaa-1111-1111-1111-111111111111",
  drop_id: "dddddddd-1111-1111-1111-111111111111",
  edition_number: 87,
  portfolio_title: "Worlds 26 Showdown",
  drop_status: "live",
  retiring_at: null,
};

const DS_RETIRED_DOSSIER: SealedDossierData = {
  id: "aaaaaaaa-2222-2222-2222-222222222222",
  drop_id: "dddddddd-2222-2222-2222-222222222222",
  edition_number: 1024,
  portfolio_title: "Holiday Heroes 2024",
  drop_status: "retired",
  retiring_at: "2025-01-08T00:00:00Z",
};

// Overflow torture-test fixture — a long multi-word title plus an
// unbreakable long word. The face type must clamp + break so it never
// crosses the gatefold fold (Epic 8 seam fix).
const DS_LONGNAME_DOSSIER: SealedDossierData = {
  id: "aaaaaaaa-3333-3333-3333-333333333333",
  drop_id: "dddddddd-3333-3333-3333-333333333333",
  edition_number: 42,
  portfolio_title:
    "The Intercontinental Championship Grand Finals Supermajor Invitational",
  drop_status: "live",
  retiring_at: null,
};

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

function Section({
  title,
  note,
  children,
}: {
  title: string;
  note?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="font-heading text-2xl font-bold">{title}</h2>
        {note ? <p className="text-sm opacity-70 mt-1">{note}</p> : null}
      </div>
      {children}
      <Separator className="mt-8" />
    </section>
  );
}

function Specimen({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-mono uppercase tracking-wider opacity-60">
        {label}
      </p>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function DesignSystemPage() {
  return (
    <main className="min-h-screen bg-brand-paper-100 text-brand-forest-900">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-10">
        <header className="space-y-2">
          <h1 className="font-heading text-4xl font-bold">
            Greenhold design system
          </h1>
          <p className="opacity-70 max-w-2xl">
            The Epic 8 working surface: canonical tokens and component
            specimens, hot-reloading from the same code the app ships.
            Heavier set-pieces live in their own sandboxes:
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {PROTOTYPES.map((p) => (
              <Link key={p.path} to={p.path}>
                <Badge variant="secondary" className="cursor-pointer">
                  {p.label}
                </Badge>
              </Link>
            ))}
          </div>
        </header>

        <Section
          title="Color — brand ramps"
          note="brand.forest / brand.gold / brand.paper / brand.ink from @greenhold/tokens."
        >
          <div className="space-y-4">
            {RAMPS.map((ramp) => (
              <Specimen key={ramp.prefix} label={ramp.name}>
                <div className="flex flex-wrap">
                  {ramp.steps.map(([step, hex]) => (
                    <div key={step} className="w-24">
                      <div
                        className="h-14 border border-black/5"
                        style={{ backgroundColor: hex }}
                      />
                      <p className="text-[0.65rem] font-mono mt-1 opacity-70">
                        {ramp.prefix}-{step}
                        <br />
                        {hex}
                      </p>
                    </div>
                  ))}
                </div>
              </Specimen>
            ))}
          </div>
        </Section>

        <Section
          title="Color — tier + state"
          note="Each is a fill / fg / accent trio. Tiers escalate common → founders."
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...TIER_TRIOS.map((t) => ({ label: t.tier, ...t })),
              ...STATE_TRIOS.map((s) => ({ label: s.state, ...s }))].map(
              (trio) => (
                <div
                  key={trio.label}
                  className="rounded-lg p-4 border border-black/5"
                  style={{ backgroundColor: trio.fill, color: trio.fg }}
                >
                  <p className="font-semibold capitalize">{trio.label}</p>
                  <p className="text-xs font-mono opacity-80">{trio.fill}</p>
                  <div
                    className="h-1.5 rounded-full mt-3"
                    style={{ backgroundColor: trio.accent }}
                  />
                </div>
              ),
            )}
          </div>
        </Section>

        <Section
          title="Typography"
          note="font-heading (Inter) for display, font-body (system) for prose, font-mono for provenance/ids."
        >
          <div className="space-y-3">
            <p className="font-heading text-5xl font-bold">Sealed until opened</p>
            <p className="font-heading text-3xl font-semibold">
              The dossier lands on a creator
            </p>
            <p className="font-heading text-xl font-medium">
              Provenance, edition, tier — on the certificate
            </p>
            <p className="font-body text-base leading-relaxed max-w-2xl">
              Body text runs on the system stack at 1rem / 1.5. Greenhold is a
              marketplace, not social media — cards link out, nothing begs for
              a like. This paragraph exists to judge measure, leading, and the
              paper-on-forest contrast.
            </p>
            <p className="font-mono text-sm opacity-80">
              mono · edition #0042 · share-serial 7 · 2026-08-03
            </p>
          </div>
        </Section>

        <Section title="Radius + elevation">
          <div className="flex flex-wrap gap-4 items-end">
            {RADII.map((r) => (
              <Specimen key={r} label={r}>
                <div className={`h-16 w-16 bg-brand-forest-500 ${r}`} />
              </Specimen>
            ))}
          </div>
          <div className="flex flex-wrap gap-6 pt-4">
            {SHADOWS.map((s) => (
              <Specimen key={s} label={s}>
                <div className={`h-16 w-28 rounded-lg bg-brand-paper-50 ${s}`} />
              </Specimen>
            ))}
          </div>
        </Section>

        <Section
          title="Primitives"
          note="The shadcn-derived set from @greenhold/components."
        >
          <div className="space-y-6">
            <Specimen label="Button — primary / secondary / destructive / ghost / disabled">
              <div className="flex flex-wrap gap-3">
                <Button variant="primary">Buy dossier</Button>
                <Button variant="secondary">List for sale</Button>
                <Button variant="destructive">Cancel listing</Button>
                <Button variant="ghost">Not now</Button>
                <Button variant="primary" disabled>
                  Processing…
                </Button>
              </div>
            </Specimen>

            <Specimen label="Badge + TierBadge">
              <div className="flex flex-wrap items-center gap-3">
                <Badge>Default</Badge>
                <Badge variant="secondary">Secondary</Badge>
                <Badge variant="outline">Outline</Badge>
                {(["common", "preferred", "premium", "founders"] as const).map(
                  (tier) => (
                    <TierBadge key={tier} tier={tier} />
                  ),
                )}
              </div>
            </Specimen>

            <Specimen label="Input + Label">
              <div className="max-w-sm space-y-2">
                <Label htmlFor="ds-input">Display name</Label>
                <Input id="ds-input" placeholder="e.g. Nova" />
              </div>
            </Specimen>

            <Specimen label="Card">
              <Card className="max-w-sm">
                <CardContent className="p-5">
                  <p className="font-heading font-semibold">Card surface</p>
                  <p className="text-sm opacity-70 mt-1">
                    General UI chrome uses the semantic tokens; brand surfaces
                    use the ramps above.
                  </p>
                </CardContent>
              </Card>
            </Specimen>

            <Specimen label="Tabs (underline style per UX-DR26)">
              <Tabs defaultValue="one" className="max-w-sm">
                <TabsList>
                  <TabsTrigger value="one">Earnings</TabsTrigger>
                  <TabsTrigger value="two">Applications</TabsTrigger>
                  <TabsTrigger value="three">Dossiers</TabsTrigger>
                </TabsList>
                <TabsContent value="one">
                  <p className="text-sm opacity-70 pt-2">Tab body.</p>
                </TabsContent>
              </Tabs>
            </Specimen>

            <Specimen label="Skeleton">
              <div className="max-w-sm space-y-2">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </Specimen>
          </div>
        </Section>

        <Section
          title="Feed surfaces"
          note="GreenholdFrame is the shared chrome for every feed card — antique-gold hairline on forest-800. Author top-left, subject top-right, provenance chip discipline."
        >
          <div className="grid md:grid-cols-2 gap-6 items-start">
            <div>
              <PinnedChip />
              <EditorialCard card={EDITORIAL_FIXTURE} />
            </div>
            <CreatorPostCard card={CREATOR_POST_FIXTURE} />
            <SilenceCard card={SILENCE_FIXTURE} />
            <GreenholdFrame>
              <p className="text-brand-paper-100 text-sm opacity-80">
                Bare GreenholdFrame — the canvas every card variant builds on.
              </p>
            </GreenholdFrame>
          </div>
        </Section>

        <Section
          title="Sealed dossier — parchment gatefold"
          note="Epic 8 design pass: parchment gatefold + pressed bordeaux wax seal. The wax seal is the visual tap target. Idle carries the optional game note; retired dims + stamps 'retired'; selected adds the gold hairline + glow. The full 5-state machine lives at /prototype/sealed-dossier."
        >
          <div className="flex flex-wrap gap-10 items-start">
            <Specimen label='state="idle" · game="Counter-Strike"'>
              <SealedDossier
                dossier={DS_LIVE_DOSSIER}
                game="Counter-Strike"
                size="md"
              />
            </Specimen>
            <Specimen label='state="selected"'>
              <SealedDossier
                dossier={DS_LIVE_DOSSIER}
                state="selected"
                size="md"
              />
            </Specimen>
            <Specimen label='state="retired"'>
              <SealedDossier
                dossier={DS_RETIRED_DOSSIER}
                state="retired"
                size="md"
              />
            </Specimen>
            <Specimen label="long name · must not cross the fold">
              <SealedDossier
                dossier={DS_LONGNAME_DOSSIER}
                game="Counter-Strike"
                size="md"
              />
            </Specimen>
          </div>
        </Section>

        <Section
          title="Patronage certificate — engraved parchment"
          note="Epic 8 design pass: the marketplace resale artifact. Fixed 320×452 intrinsic, rendered pre-scaled via the width prop. Tier palette drives the accent rule + glow ring + ink; the quote / dossier / issued / condition rows appear only when their data exists (no placeholders). Condition is a float 0→1 with 8 decimals where HIGHER = more worn (0.72138415 battle-scarred → 0.02190476 near-mint); the procedural wear (smudges, edge tears, hairline cracks) is random-but-seeded from serial+condition so it always matches the number. The signed variant ports the rotated signature scrawl — inked per a per-reveal roll at a flat 5% for all tiers (owner decision 2026-08-15, superseding the Epic 8 tier-keyed 5/15/40/100 odds)."
        >
          <div className="flex flex-wrap gap-8 items-start">
            <Specimen label='tier="common"'>
              <PatronageCertificate
                width={230}
                tier="common"
                name="Nova"
                serial="GH-71AD-C3B0"
                dossier="Signal Row · CS Cohort"
                issued="2026-06-14"
                quote="Aim holds. Editions are numbered — this one carries mine."
              />
            </Specimen>
            <Specimen label='tier="preferred"'>
              <PatronageCertificate
                width={230}
                tier="preferred"
                name="Tama Watanabe"
                serial="GH-2F19-88AA"
                dossier="Worlds 26 · LoL Cohort"
                issued="2026-05-30"
              />
            </Specimen>
            <Specimen label='tier="premium"'>
              <PatronageCertificate
                width={230}
                tier="premium"
                name="s1mple"
                serial="GH-9C04-11EF"
                dossier="Major Run · CS Cohort"
                issued="2026-04-12"
                quote="One tap at a time."
              />
            </Specimen>
            <Specimen label='tier="founders" (gold lift, no quote)'>
              <PatronageCertificate
                width={230}
                tier="founders"
                name="Zenith"
                serial="GH-0001-0001"
                dossier="Charter Cohort"
                issued="2026-01-01"
              />
            </Specimen>
            {/* Condition ladder — HIGHER = more worn. Same three data points,
                only the condition float changes, so the seeded wear reads as a
                single continuum from battle-scarred to near-mint. */}
            <Specimen label='condition=0.72138415 — battle-scarred (heavy tears + smudges)'>
              <PatronageCertificate
                width={230}
                tier="premium"
                name="Faded Nova"
                serial="GH-71AD-C3B0"
                dossier="Signal Row · CS Cohort"
                issued="2026-06-14"
                condition={0.72138415}
              />
            </Specimen>
            <Specimen label='condition=0.35000000 — mid-wear'>
              <PatronageCertificate
                width={230}
                tier="premium"
                name="Faded Nova"
                serial="GH-71AD-C3B0"
                dossier="Signal Row · CS Cohort"
                issued="2026-06-14"
                condition={0.35}
              />
            </Specimen>
            <Specimen label='condition=0.02190476 — near-mint (founders)'>
              <PatronageCertificate
                width={230}
                tier="founders"
                name="Zenith"
                serial="GH-0001-0001"
                dossier="Charter Cohort"
                issued="2026-01-01"
                condition={0.02190476}
              />
            </Specimen>
            <Specimen label="signed — rotated signature scrawl">
              <PatronageCertificate
                width={230}
                tier="founders"
                name="Zenith"
                serial="GH-0001-0001"
                dossier="Charter Cohort"
                issued="2026-01-01"
                signed
              />
            </Specimen>
          </div>
        </Section>

        <Section
          title="ParchmentPost — The Wire card"
          note="Parchment gradient + SealedDossier wear, mono author/subject row, ink body, printed-document footer (provenance left · time right). Sits on the dark wire ground; no hover motion. Successor to GreenholdFrame for feed posts — shown here on a dark ground."
        >
          <div className="rounded-lg bg-brand-forest-900 p-6">
            <div className="grid md:grid-cols-2 gap-6 items-start max-w-3xl">
              <div>
                <PinnedChip />
                <ParchmentPost
                  provenance="editorial"
                  title="First dossier goes live Friday"
                  time="2h ago"
                  pinned
                >
                  The CS cohort opens with twenty creators. Get your account
                  ready — editions are numbered.
                </ParchmentPost>
              </div>
              <ParchmentPost
                author="@nova"
                provenance="posted-by"
                provenanceName="@nova"
                time="18m ago"
              >
                Scrims all week. If the aim holds, Friday's clips are going to
                be a problem.
              </ParchmentPost>
              <ParchmentPost
                author="Greenhold"
                subject="@s1mple"
                provenance="about"
                provenanceName="@s1mple"
                time="1d ago"
              >
                A record major run and a highlight reel that refuses to age —
                the dossier subject everyone asked for.
              </ParchmentPost>
            </div>
          </div>
        </Section>

        <footer className="pb-10 text-xs opacity-60">
          Token source of truth: packages/tokens/tailwind-preset.cjs · Epic 8
          paper-cuts land here first, then sweep the app.
        </footer>
      </div>
    </main>
  );
}
